import { supabase } from '@/lib/supabase';
import { logActivity } from '@/lib/activityLog';
import { PURGEABLE_LEVEL_FILTER, PURGEABLE_LEVEL_KEYS } from '@/lib/logLevels';

export interface RetentionSettings {
  /** Ejecuta la limpieza automática cada mes. */
  autoEnabled: boolean;
  /** Meses de registros de éxito/aviso que se conservan. */
  keepMonths: number;
  /** Última vez que corrió una limpieza (automática o manual). */
  lastRunAt: string | null;
  /** Cantidad eliminada en la última limpieza. */
  lastPurgedCount: number;
}

/** Solo estos niveles se pueden vaciar. ERROR y CRÍTICO nunca se tocan. */
export const PURGEABLE_LEVELS: string[] = PURGEABLE_LEVEL_KEYS;

export const KEEP_MONTH_OPTIONS = [1, 2, 3, 6, 12];

const STORAGE_KEY = 'dkpos.activity-logs-retention';
const AUTO_INTERVAL_MS = 30 * 86400000;

const DEFAULT_SETTINGS: RetentionSettings = {
  autoEnabled: true,
  keepMonths: 1,
  lastRunAt: null,
  lastPurgedCount: 0,
};

/** Evita que dos pantallas montadas a la vez ejecuten la limpieza automática dos veces. */
let autoRunInFlight = false;

export function getRetentionSettings(): RetentionSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw) as Partial<RetentionSettings>;
    return {
      autoEnabled: parsed.autoEnabled ?? DEFAULT_SETTINGS.autoEnabled,
      keepMonths:
        typeof parsed.keepMonths === 'number' && parsed.keepMonths > 0
          ? parsed.keepMonths
          : DEFAULT_SETTINGS.keepMonths,
      lastRunAt: parsed.lastRunAt ?? null,
      lastPurgedCount:
        typeof parsed.lastPurgedCount === 'number' ? parsed.lastPurgedCount : 0,
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveRetentionSettings(patch: Partial<RetentionSettings>): RetentionSettings {
  const next = { ...getRetentionSettings(), ...patch };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Si el navegador bloquea el almacenamiento, la limpieza manual sigue funcionando.
  }
  return next;
}

function cutoffIso(months: number | null): string | null {
  if (!months || months <= 0) return null;
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString();
}

/** Cuenta cuántos registros de éxito/aviso entrarían en la limpieza. */
export async function countPurgeable(months: number | null): Promise<number> {
  let query = supabase
    .from('activity_logs')
    .select('id', { count: 'exact', head: true })
    .or(PURGEABLE_LEVEL_FILTER);

  const cutoff = cutoffIso(months);
  if (cutoff) query = query.lt('created_at', cutoff);

  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

/**
 * Elimina registros de éxito (INFO) y aviso (WARNING).
 * Con `months = null` vacía todos; con un número, solo los más antiguos que ese período.
 *
 * Primero intenta la función segura del servidor (`purge_activity_logs`), que valida
 * que el usuario sea administrador. Si esa función aún no existe en la base, cae al
 * borrado directo (que requiere una política de borrado para administradores).
 */
export async function purgeActivityLogs(
  months: number | null
): Promise<{ deleted: number; expected: number }> {
  const expected = await countPurgeable(months);

  if (expected === 0) {
    saveRetentionSettings({ lastPurgedCount: 0 });
    return { deleted: 0, expected: 0 };
  }

  let deleted = 0;

  const rpcResult = await supabase.rpc('purge_activity_logs', { p_months: months });

  if (!rpcResult.error) {
    const value = rpcResult.data as number | string | null;
    deleted = Number(value ?? 0) || 0;
  } else {
    const code = rpcResult.error.code ?? '';
    const message = rpcResult.error.message ?? '';
    const functionMissing =
      code === 'PGRST202' ||
      code === '42883' ||
      message.toLowerCase().includes('could not find the function') ||
      message.toLowerCase().includes('does not exist');

    if (!functionMissing) {
      throw new Error(
        code === '42501'
          ? 'Solo un administrador puede vaciar los registros de actividad.'
          : message || 'No se pudo vaciar el registro.'
      );
    }

    let query = supabase.from('activity_logs').delete().or(PURGEABLE_LEVEL_FILTER);
    const cutoff = cutoffIso(months);
    if (cutoff) query = query.lt('created_at', cutoff);

    const { data, error } = await query.select('id');
    if (error) throw error;
    deleted = Array.isArray(data) ? data.length : 0;
  }

  saveRetentionSettings({ lastPurgedCount: deleted });

  if (deleted > 0) {
    void logActivity({
      module: 'settings',
      action: 'activity_logs_purged',
      level: 'INFO',
      result: 'success',
      description:
        months === null
          ? `Se vaciaron ${deleted} registros de éxito y aviso.`
          : `Se eliminaron ${deleted} registros de éxito y aviso con más de ${months} mes(es).`,
      metadata: { deleted, months },
    });
  }

  return { deleted, expected };
}

/**
 * Corre la limpieza automática si ya pasó un mes desde la última.
 * Nunca lanza error: la app no debe romperse por mantenimiento.
 */
export async function runAutoPurgeIfDue(): Promise<{ ran: boolean; deleted: number }> {
  const settings = getRetentionSettings();
  if (!settings.autoEnabled) return { ran: false, deleted: 0 };
  if (autoRunInFlight) return { ran: false, deleted: 0 };

  const last = settings.lastRunAt ? new Date(settings.lastRunAt).getTime() : 0;
  const due = !last || Number.isNaN(last) || Date.now() - last >= AUTO_INTERVAL_MS;
  if (!due) return { ran: false, deleted: 0 };

  autoRunInFlight = true;
  try {
    const { deleted, expected } = await purgeActivityLogs(settings.keepMonths);
    // Solo se marca como hecha si realmente se pudo limpiar (o no había nada).
    if (expected === 0 || deleted > 0) {
      saveRetentionSettings({ lastRunAt: new Date().toISOString() });
    }
    return { ran: true, deleted };
  } catch {
    return { ran: false, deleted: 0 };
  } finally {
    autoRunInFlight = false;
  }
}